import { useState } from 'react';
import { useNavigate } from 'react-router';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { FullPolicy } from '@acorn-portal/shared/src/services/api';
import { RenewalsClient } from '@acorn-portal/shared/src/services/renewalsApi';
import {
	ICreateNewRenewalVersionResponse,
	updateRenewalDetails,
	updateRenewalRate,
} from '@acorn-portal/shared/src/store/renewal.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useAppDispatch } from './reduxHook';
import useRenewalNavigation from './useRenewalNavigation';
import { Product } from '../services/interface/Shared';

export default function useRateRenewal() {
	const [loadingRenewalQuote, setLoading] = useState(false);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { goToRenewalHome } = useRenewalNavigation();
	const rateRenewal = async ({
		renewalState,
		fullPolicy,
		excess,
		updateAddons,
		redirectToSummary = true,
	}: {
		renewalState: ICreateNewRenewalVersionResponse;
		fullPolicy: FullPolicy;
		excess?: string;
		updateAddons?: boolean;
		redirectToSummary?: boolean;
	}) => {
		setLoading(true);
		const isTaxi = fullPolicy.product === Product.TAXI;
		const client = new RenewalsClient(undefined, API);
		let newQuoteResult;
		try {
			if (!renewalState.isNewVersion) {
				newQuoteResult = await client.createNewRenewalVersion({
					policyToken: fullPolicy.policyToken,
					renewalInviteTransactionId: fullPolicy.renewalDetails?.transactionId ?? '',
					changeType: [],
				});
				dispatch(
					updateRenewalDetails({
						...renewalState,
						newRenewalTransactionId: newQuoteResult.newRenewalTransactionId,
						isNewVersion: true,
					}),
				);
			}
			let result;
			if (fullPolicy.product !== Product.COMMERCIAL_VEHICLE && !isTaxi) {
				result = await client.complexRate({
					adjustment: {
						vehicleDetails: {
							...renewalState.vehicleDetails,
							overnightAddress:
								renewalState.vehicleDetails.overnightAddress ?? renewalState.policyDetails.policyholder.address,
						},
						drivers: renewalState.drivers,
						policyDetails: renewalState.policyDetails,
						voluntaryExcessCode:
							excess ?? (renewalState.renewalRatingResponse?.coverDetails?.voluntaryExcess as unknown as string),
						addOns: renewalState.addons,
						updateAddons,
						ncbProtection: renewalState.ncbProtection,
					},
					transactionId: renewalState.newRenewalTransactionId
						? renewalState.newRenewalTransactionId
						: (newQuoteResult?.newRenewalTransactionId as unknown as string),
					policyToken: fullPolicy.policyToken,
				});
			} else {
				result = await client.complexRate({
					adjustment: {
						vehicleDetails: {
							...renewalState.vehicleDetails,
							businessMileage: isTaxi ? 0 : renewalState.vehicleDetails.businessMileage,
							overnightAddress:
								renewalState.vehicleDetails.overnightAddress ?? renewalState.policyDetails.policyholder.address,
						},
						drivers: renewalState.drivers,
						policyDetails: renewalState.policyDetails,
						voluntaryExcessCode:
							renewalState.vehicleDetails.coverDefinitionCode === 'TPO'
								? '0'
								: (excess ?? (renewalState.renewalRatingResponse?.coverDetails?.voluntaryExcess as unknown as string)),
						addOns: renewalState.addons,
						updateAddons,
						ncbProtection: renewalState.ncbProtection,
					},
					transactionId: renewalState.newRenewalTransactionId
						? renewalState.newRenewalTransactionId
						: (newQuoteResult?.newRenewalTransactionId as unknown as string),
					policyToken: fullPolicy.policyToken,
				});
			}

			if (result.hasDeclined) {
				navigate(ROUTE_PATHS.renewalMakeAChangeReferral);
				return;
			} else {
				dispatch(
					updateRenewalDetails({
						...renewalState,
						newRenewalTransactionId: renewalState.newRenewalTransactionId
							? renewalState.newRenewalTransactionId
							: (newQuoteResult?.newRenewalTransactionId as unknown as string),
						isNewVersion: true,
					}),
				);

				dispatch(updateRenewalRate(result));
				if (redirectToSummary) goToRenewalHome();
			}
		} catch (e) {
			console.error('Error in rateRenewal:', e);
			navigate(ROUTE_PATHS.error);
		} finally {
			setLoading(false);
		}
	};

	return { rateRenewal, loadingRenewalQuote };
}
